// Popup for 10 animation
import { useEffect, useRef, useState } from "react";
import { playCorrectSound } from "../../constants/gameConfig";
import { useGame } from "../../context";
import { MathZooAPI } from "../../services/api";
import "./SumToTenGame.css";

const DIFFICULTY_SETTINGS = {
  easy: { size: 4, time: 60, multiplier: 1.0 },
  medium: { size: 5, time: 50, multiplier: 1.5 },
  hard: { size: 6, time: 40, multiplier: 2.0 },
  expert: { size: 7, time: 30, multiplier: 3.0 },
};

function generatePairs(size) {
  // Generate pairs of numbers that sum to 10
  const pairs = [];
  for (let i = 1; i < 10; i++) {
    if (10 - i > 0 && 10 - i !== i) {
      pairs.push([i, 10 - i]);
    }
  }
  // Flatten and slice to fit the board size
  let numbers = pairs.flat();
  while (numbers.length < size * size) {
    numbers = numbers.concat(numbers);
  }
  numbers = numbers.slice(0, size * size);
  // Shuffle
  for (let i = numbers.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
  }
  return numbers;
}

const SumToTenGame = ({ onBack }) => {
  const { state } = useGame();
  const [difficulty, setDifficulty] = useState("easy");
  const [board, setBoard] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [selected, setSelected] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]); // [[num1, num2], ...]
  const [fading, setFading] = useState([]); // indices of cards currently fading out
  const matchSoundRef = useRef(null);
  const [timer, setTimer] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [gameState, setGameState] = useState("init"); // init, playing, win, lose
  const [scoreSaved, setScoreSaved] = useState(false);
  const [showPopup10, setShowPopup10] = useState(false);

  useEffect(() => {
    if (gameState === "playing" && timeLeft > 0) {
      const interval = setInterval(() => setTimeLeft((t) => t - 1), 1000);
      return () => clearInterval(interval);
    } else if (gameState === "playing" && timeLeft <= 0) {
      setGameState("lose");
    }
  }, [gameState, timeLeft]);

  const startGame = () => {
    const { size, time } = DIFFICULTY_SETTINGS[difficulty];
    const numbers = generatePairs(size);
    setBoard(numbers);
    setFlipped(Array(numbers.length).fill(false));
    setMatched(Array(numbers.length).fill(false));
    setSelected([]);
    setTimer(time);
    setTimeLeft(time);
    setScore(0);
    setCombo(0);
    setGameState("playing");
  };

  const handleCardClick = (idx) => {
    if (matched[idx] || gameState !== "playing") return;
    // If already selected, unselect it
    if (selected.includes(idx)) {
      const newSelected = selected.filter((i) => i !== idx);
      setSelected(newSelected);
      // For easy/medium, also flip back
      if (difficulty === "hard" || difficulty === "expert") {
        const newFlipped = [...flipped];
        newFlipped[idx] = false;
        setFlipped(newFlipped);
      }
      return;
    }
    // If already 2 selected, ignore
    if (selected.length === 2) return;
    const newSelected = [...selected, idx];
    setSelected(newSelected);
    const newFlipped = [...flipped];
    newFlipped[idx] = true;
    setFlipped(newFlipped);
    if (newSelected.length === 2) {
      const [i1, i2] = newSelected;
      if (board[i1] + board[i2] === 10) {
        // Match
        setShowPopup10(true);
        setTimeout(() => setShowPopup10(false), 700);
        setFading([i1, i2]);
        setTimeout(() => {
          const newMatched = [...matched];
          newMatched[i1] = true;
          newMatched[i2] = true;
          setMatched(newMatched);
          setCombo((c) => c + 1);
          setScore((s) => s + 100 + (combo + 1) * 50);
          setMatchedPairs((pairs) => [...pairs, [board[i1], board[i2]]]);
          playCorrectSound();
          setSelected([]);
          // Make matched cards disappear after 1s
          setTimeout(() => {
            const newFlipped2 = [...flipped];
            newFlipped2[i1] = false;
            newFlipped2[i2] = false;
            setFlipped(newFlipped2);
            setFading([]);
            if (newMatched.filter(Boolean).length === board.length) {
              setGameState("win");
            }
          }, 1000);
        }, 500);
      } else {
        // Not match
        setTimeout(() => {
          const resetFlipped = [...flipped];
          resetFlipped[i1] = false;
          resetFlipped[i2] = false;
          setFlipped(resetFlipped);
          setCombo(0);
          setSelected([]);
        }, 800);
      }
    }
  };

  const calcFinalScore = () => {
    const { multiplier } = DIFFICULTY_SETTINGS[difficulty];
    const base = 100 * (matched.filter(Boolean).length / 2);
    const timeBonus = timeLeft * 10;
    const comboBonus = combo * 50;
    return Math.floor((base + timeBonus + comboBonus) * multiplier);
  };

  // 保存分數到後端
  useEffect(() => {
    if (
      (gameState === "win" || gameState === "lose") &&
      !scoreSaved &&
      state?.user?.id
    ) {
      const payload = {
        gameType: "sumToTen",
        question: {
          difficulty,
          boardSize: DIFFICULTY_SETTINGS[difficulty].size,
        },
        correct: gameState === "win",
        usedHint: false,
        scoreGained: calcFinalScore(),
        starsGained: gameState === "win" ? 1 : 0,
        timeSpent: timer - timeLeft,
      };
      MathZooAPI.recordGame(state.user.id, payload)
        .then(() => setScoreSaved(true))
        .catch(() => {});
    }
    if (gameState === "playing") setScoreSaved(false);
    // eslint-disable-next-line
  }, [gameState]);

  return (
    <div className="sum-to-ten-game">
      {/* Back button on top */}
      <div className="top-control-bar">
        <button className="back-btn" onClick={() => setGameState("init")}>
          ← 返回
        </button>
        {gameState === "playing" && (
          <button className="pause-btn" onClick={() => setGameState("paused")}>
            ⏸ 暫停
          </button>
        )}
      </div>
      <audio ref={matchSoundRef} src="/sounds/match.mp3" preload="auto" />
      {gameState === "init" && (
        <div className="game-setup">
          <h2>湊十對對碰</h2>
          <label>選擇難度：</label>
          <div className="difficulty-buttons">
            {Object.entries(DIFFICULTY_SETTINGS).map(([key, { size }]) => (
              <button
                key={key}
                className={`difficulty-btn${
                  difficulty === key ? " selected" : ""
                }`}
                onClick={() => setDifficulty(key)}
              >
                {key === "easy" && "簡單"}
                {key === "medium" && "普通"}
                {key === "hard" && "困難"}
                {key === "expert" && "專家"}
                <span style={{ fontSize: 12, marginLeft: 6 }}>
                  ({size}x{size})
                </span>
              </button>
            ))}
          </div>
          <button className="start-game-btn" onClick={startGame}>
            開始遊戲
          </button>
          <button
            className="exit-game-btn"
            onClick={onBack}
            style={{ marginTop: 24 }}
          >
            離開遊戲
          </button>
        </div>
      )}
      {gameState === "playing" && (
        <div className="game-board">
          <div className="game-info">
            <span>剩餘時間: {timeLeft}s</span>
            <span>分數: {score}</span>
            <span>連擊: {combo}</span>
          </div>
          <div
            className="board-grid"
            style={{
              gridTemplateColumns: `repeat(${DIFFICULTY_SETTINGS[difficulty].size}, 1fr)`,
            }}
          >
            {board.map((num, idx) => {
              // 簡單/普通模式直接顯示數字
              const alwaysShow =
                difficulty === "easy" || difficulty === "medium";
              // For easy/medium, add visible and selected/hover classes
              let cardClass = "card";
              if (alwaysShow || flipped[idx] || matched[idx])
                cardClass += " flipped";
              if (matched[idx]) cardClass += " matched";
              if (alwaysShow) cardClass += ` ${difficulty}-visible`;
              if (alwaysShow && selected.includes(idx))
                cardClass += " selected";
              if (fading.includes(idx)) cardClass += " fading";
              return (
                <div
                  key={idx}
                  className={cardClass}
                  onClick={() => handleCardClick(idx)}
                >
                  <div className="card-inner">
                    <div className="card-front">{num}</div>
                    <div className="card-back">?</div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Popup 10 animation */}
          {showPopup10 && <div className="popup-10">10</div>}
          {/* 動態顯示已配對的卡片 */}
          <div className="matched-pairs-bar">
            <span>已配對：</span>
            {matchedPairs.map((pair, i) => (
              <span key={i} className="matched-pair">
                {pair[0]}+{pair[1]}
              </span>
            ))}
          </div>
        </div>
      )}

      {gameState === "paused" && (
        <div className="game-paused">
          <h2>遊戲暫停</h2>
          <button
            className="resume-btn"
            onClick={() => setGameState("playing")}
          >
            ▶️ 繼續
          </button>
        </div>
      )}
      {gameState === "win" && (
        <div className="game-result win">
          <h2>勝利！</h2>
          <div>總分數: {calcFinalScore()}</div>
          <div>
            <button className="start-game-btn" onClick={startGame}>
              再玩一次
            </button>
          </div>
        </div>
      )}
      {gameState === "lose" && (
        <div className="game-result lose">
          <h2>失敗！</h2>
          <div>總分數: {calcFinalScore()}</div>
          <div>
            <button className="start-game-btn" onClick={startGame}>
              再玩一次
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SumToTenGame;
